/**
 * Navigation utility functions for consistent handling of navigation throughout the application
 */

/**
 * Navigates to the homepage while ensuring clean state
 * - Clears hash fragments
 * - Clears query parameters
 * - Handles scroll position
 * @param {object} navigate - React Router's navigate function (optional)
 * @returns {void}
 */
export const navigateToCleanHome = (navigate) => {  
  // First, disable automatic scrolling that might interfere
  const originalScrollRestoration = window.history.scrollRestoration;
  window.history.scrollRestoration = 'manual';

  // Use React Router's navigate function if provided
  if (navigate && window.location.pathname !== '/') {
    // If we're not on homepage, use React Router to navigate
    // This ensures proper route handling
    navigate('/', { 
      state: { 
        fromLogoClick: true, 
        shouldScrollToTop: true,
        skipContactScroll: true, // Add explicit flag to prevent contact form scroll
        timestamp: Date.now() // Add timestamp to ensure state is unique
      },
      replace: true // Replace current history entry to prevent back button issues
    });
    
    // Restore original scroll restoration after a delay
    setTimeout(() => {
      window.history.scrollRestoration = originalScrollRestoration;
    }, 500);
    
    return; // Exit early as navigation will trigger route change
  }
  
  // If navigate function not provided or already on homepage, clean up manually
  
  // Clear any hash and query params from the URL
  if (window.location.hash || window.location.search) {
    window.history.replaceState({
      fromLogoClick: true, 
      shouldScrollToTop: true,
      skipContactScroll: true, // Add explicit flag to prevent contact form scroll
      timestamp: Date.now()
    }, document.title, '/');
  } else {
    // Even without hash, we should update the state
    window.history.replaceState({
      fromLogoClick: true, 
      shouldScrollToTop: true,
      skipContactScroll: true, // Add explicit flag to prevent contact form scroll
      timestamp: Date.now()
    }, document.title, window.location.pathname);
  }
  
  // Clear any scroll-related flags to ensure clean navigation
  window.__isFormFocused = false;
  window.__textareaActive = false;
  window.__formAreaClicked = false;
  
  // Clear body classes that might affect scrolling
  document.body.classList.remove('form-input-focused');
  document.body.classList.remove('textarea-focused');
    // Clear any scroll-related flags to prevent form interactions from interfering
  window.__isFormFocused = false;
  window.__textareaActive = false;
  window.__formAreaClicked = false;
  document.body.classList.remove('form-input-focused');
  document.body.classList.remove('textarea-focused');
  
  // Force scroll to top using multiple methods for reliability
  try {
    // Method 1: Basic scroll
    window.scrollTo(0, 0);
    
    // Method 2: Scroll with options
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' // Use instant for more reliable scrolling
    });
    
    // Method 3: Additional scroll attempts with delays
    // Using more frequent attempts at the beginning to ensure it takes effect
    const scrollAttempts = [10, 30, 50, 100, 200, 300, 500];
    scrollAttempts.forEach(delay => {
      setTimeout(() => {
        // Check if any form interaction is active before attempting scroll
        if (!window.__isFormFocused && !window.__textareaActive && !window.__formAreaClicked) {
          window.scrollTo(0, 0);
          console.log(`🔄 Scroll attempt at ${delay}ms`);
        }
      }, delay);
    });
  } catch (e) {
    console.error('Error during scroll:', e);
    // Fallback to most basic scroll
    window.scrollTo(0, 0);
  }
  
  // Restore original scroll restoration after all scroll attempts
  setTimeout(() => {
    window.history.scrollRestoration = originalScrollRestoration;
  }, 600);
};

/**
 * Safe scroll to top utility that works in all browsers
 * and doesn't trigger unwanted side effects
 */
export const safeScrollToTop = (options = {}) => {
  const { immediate = true, smooth = false } = options;
  
  try {
    // Use immediate scroll for reliability
    if (immediate) {
      window.scrollTo(0, 0);
    } else {
      // Use smooth scroll if requested
      window.scrollTo({
        top: 0,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
    
    // Add a fallback scroll after a short delay
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  } catch (e) {
    console.error('Error during scroll:', e);
    // Fallback to basic scroll
    window.scrollTo(0, 0);
  }
};
